import { useMemo } from "react";
import { useGetTransactionVolumeChart } from "../services/AnalyticsPageService";
import storage from "services/storage";
import dayjs from "dayjs";
import { uniq } from "lodash";
import { GroupVerticalBarChart } from "components";
import { useLocation } from "react-router-dom";
import { paramParsify } from "utils/queryParams";
import { formatDate } from "utils/formatDate";

const TransactionVolumeSection = () => {
  const companyIdLocalStorage = storage.getCompanyID();
  const location = useLocation();
  const queryParams = paramParsify(location?.search);

  const { response: buyResp } = useGetTransactionVolumeChart({
    id: companyIdLocalStorage,
    transaction_type: "buy",
  });
  const { response: sellResp } = useGetTransactionVolumeChart({
    id: companyIdLocalStorage,
    transaction_type: "sell",
  });

  const datePeriod = useMemo(() => {
    const startPeriod = formatDate.toFullDate(
      queryParams?.period_start || Date.now(),
    );
    const endPeriod = formatDate.toFullDate(
      queryParams?.period_end || Date.now(),
    );

    return (
      <p className='text-[#5F737F] text-sm'>{`${startPeriod} - ${endPeriod}`}</p>
    );
  }, [queryParams]);

  const chartData = useMemo(() => {
    let labels = [];
    const buyData = [];
    const sellData = [];

    // listing labels from buy & sell
    buyResp?.data?.forEach((el) => {
      labels?.push(dayjs(el?.period).format("YYYY-MM-DD"));
    });
    sellResp?.data?.forEach((el) => {
      labels?.push(dayjs(el?.period).format("YYYY-MM-DD"));
    });

    // modifiy labels
    labels = uniq(labels)
      ?.sort((a, b) => a - b)
      ?.map((el) => dayjs(el)?.format("DD MMM"));

    // insert buy & sell data based on modified labels
    labels?.forEach((label) => {
      const foundBuyData =
        buyResp?.data?.find(
          (el) => dayjs(el?.period)?.format("DD MMM") === label,
        )?.amount ?? 0;
      const foundSellData =
        sellResp?.data?.find(
          (el) => dayjs(el?.period)?.format("DD MMM") === label,
        )?.amount ?? 0;

      buyData.push(foundBuyData);
      sellData.push(foundSellData);
    });

    // create datasets based on generated buy, sell, and modified label
    const datasets = [
      {
        label: "Buy",
        backgroundColor: "#039E5A",
        data: buyData,
        datalabels: {
          display: false,
        },
      },
      {
        label: "Sell",
        backgroundColor: "#E52A49",
        data: sellData,
        datalabels: {
          display: false,
        },
      },
    ];

    return {
      labels,
      datasets,
    };
  }, [buyResp, sellResp]);

  return (
    <div className='section h-full'>
      <p className='font-bold text-lg mb-6'>Transaction Shares Volume</p>
      <div className='flex flex-row justify-between mb-4'>
        {datePeriod}
        <div className='flex flex-row gap-4'>
          <div className='flex flex-row gap-2 items-center'>
            <div className='rounded-full w-1 h-1 p-1 bg-[#039E5A]' />
            <p className='text-xs'>Buy</p>
          </div>
          <div className='flex flex-row gap-2 items-center'>
            <div className='rounded-full w-1 h-1 p-1 bg-[#E52A49]' />
            <p className='text-xs'>Sell</p>
          </div>
        </div>
      </div>
      <p className='text-[#5F737F] text-[9px] ml-5 mb-3'>Volume</p>
      <GroupVerticalBarChart id='transaction-volume-chart' data={chartData} />
    </div>
  );
};

export default TransactionVolumeSection;
